import { defineComponent as _defineComponent } from 'vue'
import { ref, toRefs, computed, watch, onMounted } from "@nuxtjs/composition-api";
import { toCurrencyFormat, toNumericFormatBTC } from "@/core/helpers/GlobalHelpers";
import { useMobile } from "~/core/composables/useMobile";
import { Purchase } from "~/core/types/purchase-popup-2/Purchase";
import { UserBalance } from "~/core/models/purchase-popup-2/UserBalance";
import { ExternalWalletPayment } from "~/core/models/purchase-popup-2/ExternalWalletPayment";
import { UseExtraBalancesTypes } from "~/core/models/purchase-popup-2/UseExtraBalancesTypes";
import { PaymentMethodEnum } from "~/core/types/purchase-popup-2/PaymentMethodEnum";
import MunicipalityTx from "~/core/models/purchase-popup-2/MunicipalityTx";
import Web3MunicipalityTx from "~/core/models/purchase-popup-2/Web3MunicipalityTx";

interface Props {
    purchase: Purchase;
    userBalance: UserBalance;
    transactionObject: MunicipalityTx | Web3MunicipalityTx | ExternalWalletPayment;
    paymentMethod: string;
    readonly: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PaymentSummary',
  props: {
    purchase: null,
    userBalance: { default: () => null },
    transactionObject: null,
    paymentMethod: null,
    readonly: { type: Boolean }
  },
  emits: [
    'onClickBack',
    'onStartLoading',
    'onStopLoading',
    'onSuccessFullPayment',
],
  setup(__props: any, { emit: $emit }) {

const props = __props as { purchase: Purchase, userBalance: UserBalance, transactionObject: MunicipalityTx | Web3MunicipalityTx | ExternalWalletPayment, paymentMethod: string, readonly: boolean };





const {
    purchase,
    userBalance,
    transactionObject,
    paymentMethod,
    readonly,
} = toRefs(props);

const FEE_VALUE = 3;
const isMobile = useMobile();
const gasFeeVisible = ref(false);
const enabledJustNow = ref(false);
const isExtraBalanceChecking = ref(false);

const utilityAsset = computed(() => {
    return transactionObject.value._isUsingExtraBalance === UseExtraBalancesTypes.BALANCE_GYMNET
        ? transactionObject.value.priceInfo.splitAmountGymnet.gymnet
        : transactionObject.value.priceInfo.splitAmountUtility.gymnet
})
const usdtAsset = computed(() => {
    if (transactionObject.value.isUsingExtraBalance) {
        return transactionObject.value._isUsingExtraBalance === UseExtraBalancesTypes.BALANCE_GYMNET
            ? transactionObject.value.priceInfo.splitAmountGymnet.usdt
            : transactionObject.value.priceInfo.splitAmountUtility.usdt;
    }
    return transactionObject.value.priceInfo.totalAmount;
})
const mainAsset = computed(() => {
    let asset = transactionObject.value.priceInfo.totalAmount;
    if (transactionObject.value.isUsingExtraBalance) {
        asset = transactionObject.value.priceInfo.splitAmountUtility.usdt;
    }
    if (
        isPaymentMethodInternal &&
        transactionObject.value.priceInfo.gasAmount &&
        transactionObject.value.priceInfo.gasAmount.value > userBalance.value.bnb.value) {
        asset = asset.copy(asset.value + FEE_VALUE);
    }
    return asset;
})

const isPaymentStatusCompleted = computed(() => transactionObject.value.isCreated);
const isPaymentStatusCreated = computed(() => transactionObject.value.isCompleted);
const isPaymentMethodInternal = computed(() => paymentMethod.value === PaymentMethodEnum.INTERNAL);
const gasAmount = computed(() => transactionObject.value.priceInfo.gasAmount);
const hasEnoughBNBFeeBalance = computed(() => {
    return userBalance.value.bnb.value >= transactionObject.value.priceInfo.gasAmount.value;
});
const isShowPurchaseButton = computed(() => {
    return (!readonly.value || isPaymentStatusCreated.value) && paymentMethod.value !== PaymentMethodEnum.EXTERNAL;
})
const hasEnoughBUSDFeeBalance = computed(() => {
    return Number(userBalance.value.usdt.value) >= Number(usdtAsset.value.value) + FEE_VALUE;
})
const totalUSD = computed(() => {
    let totalUSD = mainAsset.value.usd;
    if (transactionObject.value.isUsingExtraBalance) {
        totalUSD += utilityAsset.value.usd;
    }
    return totalUSD;
})
const priceData = computed(() => {
    return transactionObject.value.isUsingExtraBalance && utilityAsset.value
        ? [mainAsset.value, utilityAsset.value]
        : [mainAsset.value];
})
const notHaveBNBForGas = computed(() => {
    return isPaymentMethodInternal.value && transactionObject.value.priceInfo.gasAmount.value > userBalance.value.bnb.value;
})

const handleEnablePayment = async () => {
    gasFeeVisible.value = false;
    try {
        $emit('onStartLoading');

        if (!transactionObject.value.isUsdtEnabled) {
            await transactionObject.value.approve();
        }

        if (transactionObject.value.isUsingExtraGymnetBalance && !transactionObject.value.isGymnetEnabled) {
            await transactionObject.value.approveGymnet();
        }

        transactionObject.value.clearCache();
        await transactionObject.value.estimateGas();
        enabledJustNow.value = true;
        $emit('onStopLoading');
    } catch (e) {
        $emit('onStopLoading', e);
    } finally {
        gasFeeVisible.value = true;
    }
}

const handlePay = async () => {
    gasFeeVisible.value = false;
    try {
        $emit('onStartLoading');
        await transactionObject.value.send();
        $emit('onSuccessFullPayment');
        $emit('onStopLoading');
    } catch (e) {
        $emit('onStopLoading', e);
    } finally {
        gasFeeVisible.value = true;
    }
}

const useExtraBalance = async (extraType: UseExtraBalancesTypes) => {
    isExtraBalanceChecking.value = true;

    try {
        transactionObject.value.setExtraBalance(extraType);

        if (extraType === UseExtraBalancesTypes.BALANCE_GYMNET) {
            const gymnetAmountToCheck = transactionObject.value.priceInfo.splitAmountGymnet.gymnet.value;
            await transactionObject.value.checkGymnetAllowance(gymnetAmountToCheck);
        }
    } finally {
        isExtraBalanceChecking.value = false;
    }
}

onMounted(() => {
    setTimeout(() => {
        gasFeeVisible.value = true;
    }, 100);
})

watch(
    () => transactionObject.value.isEnabled,
    () => {
        setTimeout(() => {
            gasFeeVisible.value = true;
        }, 100);
    },
    { immediate: true }
);

return { __sfc: true,$emit, props, purchase, userBalance, transactionObject, paymentMethod, readonly, FEE_VALUE, isMobile, gasFeeVisible, enabledJustNow, isExtraBalanceChecking, utilityAsset, usdtAsset, mainAsset, isPaymentStatusCompleted, isPaymentStatusCreated, isPaymentMethodInternal, gasAmount, hasEnoughBNBFeeBalance, isShowPurchaseButton, hasEnoughBUSDFeeBalance, totalUSD, priceData, notHaveBNBForGas, handleEnablePayment, handlePay, useExtraBalance, toCurrencyFormat, toNumericFormatBTC, PaymentMethodEnum }
}

})